import { useMemo, useRef, useState } from 'react';
import { Checkbox } from '@/components/ui/Checkbox';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { IconButton } from '@/components/ui/IconButton';
import { IconArrowUp } from '@/components/ui/Icon';
import { SECTIONS } from '@/components/layout/nav-config';
import { useNavPrefs } from '@/shared/store/navPrefsStore';

type Props = { embedded?: boolean };

// A small vertical drag-and-drop list. Renders one row per `items` entry and
// lets the caller reorder by giving it a new ordered array. Uses native HTML5
// DnD so we don't pull in a third-party library. Each row has its own "hide"
// toggle rendered by the caller via `renderExtra`.
type DragListProps<T> = {
  items: T[];
  keyOf: (item: T) => string;
  nameOf: (item: T) => string;
  listLabel: string;
  onReorder: (next: T[]) => void;
  renderLabel: (item: T) => React.ReactNode;
  renderExtra?: (item: T) => React.ReactNode;
};

function DragList<T>({ items, keyOf, nameOf, listLabel, onReorder, renderLabel, renderExtra }: DragListProps<T>) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);

  function move(from: number, to: number) {
    if (from === to) return;
    const next = items.slice();
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    onReorder(next);
  }

  // A button click: move, then put focus back on that row's button (the keyed
  // row moves in the DOM, and a button that becomes disabled drops focus). If
  // the wanted button is now disabled, the row's other one gets it.
  function moveByButton(i: number, dir: 'up' | 'down') {
    const key = keyOf(items[i]);
    const to = dir === 'up' ? i - 1 : i + 1;
    move(i, to);
    const wanted = dir === 'up' ? to > 0 : to < items.length - 1;
    const focusDir = wanted ? dir : dir === 'up' ? 'down' : 'up';
    requestAnimationFrame(() => {
      listRef.current?.querySelector<HTMLElement>(`[data-move="${key}|${focusDir}"]`)?.focus();
    });
  }

  return (
    <div ref={listRef} role="list" aria-label={listLabel} className="flex flex-col gap-1">
      {items.map((it, i) => {
        const isDragging = dragIdx === i;
        const isTarget = overIdx === i && dragIdx !== null && dragIdx !== i;
        return (
          <div
            key={keyOf(it)}
            role="listitem"
            draggable
            onDragStart={(e) => {
              setDragIdx(i);
              // The text data is ignored by our own handler but is required
              // for drag to work consistently in Firefox.
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', keyOf(it));
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (overIdx !== i) setOverIdx(i);
            }}
            onDragLeave={() => {
              if (overIdx === i) setOverIdx(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragIdx !== null) move(dragIdx, i);
              setDragIdx(null);
              setOverIdx(null);
            }}
            onDragEnd={() => {
              setDragIdx(null);
              setOverIdx(null);
            }}
            className={`flex items-center gap-3 px-3 py-2 rounded-md border text-[0.8125rem] cursor-grab active:cursor-grabbing transition-colors ${
              isDragging ? 'opacity-40 bg-slate-50' : ''
            } ${isTarget ? 'border-primary bg-primary/10' : 'border-border-light bg-surface'}`}
          >
            <span className="text-muted select-none" aria-hidden>⋮⋮</span>
            <div className="flex-1 min-w-0 truncate">{renderLabel(it)}</div>
            {renderExtra && <div className="shrink-0">{renderExtra(it)}</div>}
            {/* Keyboard and single-pointer alternative to dragging. */}
            <div className="shrink-0 inline-flex items-center gap-1">
              <IconButton label={`Move ${nameOf(it)} up`} data-move={`${keyOf(it)}|up`} disabled={i === 0} onClick={() => moveByButton(i, 'up')}>
                <IconArrowUp size={13} />
              </IconButton>
              <IconButton label={`Move ${nameOf(it)} down`} data-move={`${keyOf(it)}|down`} disabled={i === items.length - 1} onClick={() => moveByButton(i, 'down')}>
                <IconArrowUp size={13} className="rotate-180" />
              </IconButton>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MenuDesignPage({ embedded = false }: Props = {}) {
  const prefs = useNavPrefs();
  const [subSectionKey, setSubSectionKey] = useState<string>(SECTIONS[0]?.key || '');

  // Current main-nav order = stored order first, then defaults appended for
  // new sections that shipped after the user last customized. Same
  // append-new behaviour as the store's helper, duplicated here so the UI
  // doesn't round-trip through the store just to render.
  const mainOrdered = useMemo(() => {
    const byKey = new Map(SECTIONS.map((s) => [s.key, s]));
    const seen = new Set<string>();
    const out = [] as typeof SECTIONS;
    for (const k of prefs.mainOrder) {
      const s = byKey.get(k);
      if (s && !seen.has(k)) { out.push(s); seen.add(k); }
    }
    for (const s of SECTIONS) if (!seen.has(s.key)) out.push(s);
    return out;
  }, [prefs.mainOrder]);

  const subSection = SECTIONS.find((s) => s.key === subSectionKey);
  const subOrdered = useMemo(() => {
    if (!subSection) return [];
    const order = prefs.subOrder[subSection.key] || [];
    const byKey = new Map(subSection.sub.map((it) => [it.to, it]));
    const seen = new Set<string>();
    const out = [] as typeof subSection.sub;
    for (const k of order) {
      const it = byKey.get(k);
      if (it && !seen.has(k)) { out.push(it); seen.add(k); }
    }
    for (const it of subSection.sub) if (!seen.has(it.to)) out.push(it);
    return out;
  }, [subSection, prefs.subOrder]);

  const body = (
    <div className="space-y-6 text-[0.8125rem]">
      {/* Compact mode toggle */}
      <div className="flex items-center justify-between px-3 py-2.5 rounded-md border border-border-light bg-surface">
        <div>
          <div className="font-semibold text-primary-text">Compact main nav</div>
          <div className="text-meta text-muted mt-0.5">Collapse tabs to icon-only pills to save horizontal space.</div>
        </div>
        <Checkbox
          checked={prefs.compact}
          onChange={(e) => prefs.setCompact(e.target.checked)}
          aria-label="Compact main nav"
        />
      </div>

      {/* Main tabs reorder + hide */}
      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="font-semibold text-primary-text">Main tabs</h3>
          <span className="text-meta text-muted">Drag or use the arrows to reorder · checkbox hides</span>
        </div>
        <DragList
          items={mainOrdered}
          keyOf={(s) => s.key}
          nameOf={(s) => s.label}
          listLabel="Main tabs"
          onReorder={(next) => prefs.setMainOrder(next.map((s) => s.key))}
          renderLabel={(s) => {
            return (
              <div className="flex items-center gap-2">
                {/* Same webfont glyph + module color as the main nav */}
                <i className={`${s.fa} ${s.faColor} text-[0.875rem] w-4 text-center leading-none`} aria-hidden="true" />
                <span>{s.label}</span>
              </div>
            );
          }}
          renderExtra={(s) => (
            <Checkbox
              checked={!prefs.hidden.includes(s.key)}
              onChange={(e) => prefs.setHidden(s.key, !e.target.checked)}
              className="text-muted"
              label={<><span>Visible</span></>}
            />
          )}
        />
      </div>

      {/* Sub-nav reorder + hide, per section */}
      <div>
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h3 className="font-semibold text-primary-text shrink-0">Sub-menu items</h3>
          <div className="flex-1 max-w-[240px]">
            <Select value={subSectionKey} onChange={(e) => setSubSectionKey(e.target.value)}>
              {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </Select>
          </div>
        </div>
        {subOrdered.length === 0 ? (
          <div className="text-muted text-[0.75rem] px-1 py-2">No sub-items in this section.</div>
        ) : (
          <DragList
            items={subOrdered}
            keyOf={(it) => it.to}
            nameOf={(it) => it.label}
            listLabel="Sub-menu items"
            onReorder={(next) => subSection && prefs.setSubOrder(subSection.key, next.map((it) => it.to))}
            renderLabel={(it) => (
              <div className="flex items-center gap-2">
                <span>{it.label}</span>
                <span className="text-meta text-muted font-numeric truncate">{it.to}</span>
              </div>
            )}
            renderExtra={(it) => (
              <Checkbox
                checked={!prefs.hidden.includes(it.to)}
                onChange={(e) => prefs.setHidden(it.to, !e.target.checked)}
                className="text-muted"
                label={<><span>Visible</span></>}
              />
            )}
          />
        )}
      </div>

      <div className="flex items-center justify-between pt-1">
        <div className="text-meta text-muted">Saved to your account — synced across all your devices.</div>
        <Button variant="secondary" onClick={() => prefs.reset()}>Reset to defaults</Button>
      </div>
    </div>
  );

  if (embedded) return body;

  return (
    <div>
      <PageHeader title="Menu design" description="Customize the navigation layout." />
      {body}
    </div>
  );
}
